/**
 * Chat Header - WhatsApp-style header for conversation
 */

import React from 'react';
import { Typography, Avatar, Dropdown, Button } from 'antd';
import { MoreOutlined, UserOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { PresenceIndicator } from './PresenceIndicator';
import './ChatHeader.css';

const { Text } = Typography;

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const normalizeStatus = (s: string | undefined): PresenceStatus =>
  ['online', 'away', 'busy', 'offline'].includes(s || '') ? (s as PresenceStatus) : 'offline';

interface ChatHeaderProps {
  channelId: string;
  onBackToList?: () => void;
  showBackButton?: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ channelId, onBackToList, showBackButton }) => {
  const { presence } = useChatWebSocket();

  const { data } = useQuery(
    ['channel-details', channelId],
    () => api.get(`/api/chat/channels/${channelId}`).then(res => res.data),
    { enabled: !!channelId }
  );

  const { data: membersData } = useQuery(
    ['channel-members', channelId],
    () => api.get(`/api/chat/channels/${channelId}/members`).then(res => res.data),
    { enabled: !!channelId }
  );

  const channel = data?.channel;
  const members = membersData?.members || [];

  const getDisplayName = () => {
    if (!channel) return 'Chat';
    if (channel.type === 'direct') {
      const other = members.find((m: any) => m.id !== channel.created_by);
      if (other) {
        const fn = other.first_name || other.firstName || '';
        const ln = other.last_name || other.lastName || '';
        return `${fn} ${ln}`.trim() || other.email || 'Contact';
      }
      const ou = channel.other_user || channel.otherUser;
      if (ou) {
        const fn = ou.first_name || (ou as any).firstName || '';
        const ln = ou.last_name || (ou as any).lastName || '';
        return `${fn} ${ln}`.trim() || ou.email || 'Contact';
      }
    }
    return channel.name || 'Group';
  };

  const getOtherMember = () => {
    if (channel?.type !== 'direct') return null;
    const other = members.find((m: any) => m.id !== channel.created_by);
    if (other) return other;
    return channel.other_user || channel.otherUser;
  };

  const otherMember = getOtherMember();
  const status = otherMember
    ? normalizeStatus(presence[otherMember.id]?.status || otherMember.presence_status || (otherMember as any).presenceStatus)
    : 'offline';

  const menuItems = [
    { key: 'view-contact', label: 'View contact' },
    { key: 'mute', label: 'Mute notifications' },
    { key: 'clear', label: 'Clear chat' },
  ];

  return (
    <div className="whatsapp-chat-header">
      {showBackButton && onBackToList && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          className="chat-header-back-btn"
          onClick={onBackToList}
          aria-label="Back to conversations"
        />
      )}
      <div className="chat-header-avatar">
        {channel?.type === 'direct' && otherMember ? (
          <PresenceIndicator status={status} size="small">
            <Avatar size={40} icon={<UserOutlined />} className="header-avatar" />
          </PresenceIndicator>
        ) : (
          <Avatar size={40} icon={<UserOutlined />} className="header-avatar group-avatar" />
        )}
      </div>
      <div className="chat-header-info">
        <Text strong className="chat-header-name">
          {getDisplayName()}
        </Text>
        <Text className="chat-header-status">
          {channel?.type === 'direct'
            ? status === 'online'
              ? 'Online'
              : status === 'away'
              ? 'Away'
              : status === 'busy'
              ? 'Busy'
              : 'Offline'
            : `${members.length} members`}
        </Text>
      </div>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
        <button type="button" className="chat-header-menu-btn">
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );
};
